<template>
  <v-dialog v-model="internalValue" max-width="800px" scrollable>
    <v-card class="ds-dialog">
      <!-- Header -->
      <v-card-title
        class="ds-dialog-header d-flex justify-space-between align-center"
      >
        <div class="header-content pa-6" data-tour="det-header">
          <div class="d-flex align-center justify-space-between w-100">
            <div class="d-flex align-center">
              <v-avatar size="80" class="mr-4 elevation-4">
                <img :src="photoUrl" :alt="person.name" />
              </v-avatar>
              <div>
                <h2 class="text-h5 font-weight-bold mb-1">{{ person.name }}</h2>
                <div class="text-subtitle-1 mb-1">
                  <v-chip small outlined dark class="mr-2">
                    {{ cardType === "médico" ? "Médico" : "Paciente" }}
                  </v-chip>
                </div>
                <div class="text-subtitle-2">
                  <v-icon small dark class="mr-1">mdi-cake-variant</v-icon>
                  {{ personAge }} anos
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="d-flex align-center">
          <v-btn
            icon
            dark
            class="close-btn"
            aria-label="Ver tour desta ficha"
            @click="startTour"
          >
            <v-icon>mdi-help-circle-outline</v-icon>
          </v-btn>
          <v-btn icon dark class="close-btn" @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <!-- Content -->
      <v-card-text class="ds-dialog-body">
        <!-- Informações Básicas -->
        <section class="ds-form-section" data-tour="det-info">
          <div class="ds-form-section__head">
            <div class="ds-form-section__title">
              <v-icon small color="primary" class="mr-2">mdi-account-circle</v-icon>
              <span>Informações Pessoais</span>
            </div>
          </div>
          <v-row dense>
            <v-col cols="12" sm="6">
              <div class="detail-item">
                <div class="detail-label">
                  <v-icon small class="mr-2">mdi-account</v-icon>
                  Nome Completo
                </div>
                <div class="detail-value">
                  {{ person.name || "Não informado" }}
                </div>
              </div>
            </v-col>

            <v-col cols="12" sm="6">
              <div class="detail-item">
                <div class="detail-label">
                  <v-icon small class="mr-2">mdi-calendar</v-icon>
                  Data de Nascimento
                </div>
                <div class="detail-value">
                  {{ formatDateBr(person.birth_date) }}
                </div>
              </div>
            </v-col>

            <v-col cols="12" sm="6">
              <div class="detail-item">
                <div class="detail-label">
                  <v-icon small class="mr-2">mdi-human-male-female</v-icon>
                  Gênero
                </div>
                <div class="detail-value">
                  {{ person.gender || "Não informado" }}
                </div>
              </div>
            </v-col>

            <v-col cols="12" sm="6">
              <div class="detail-item">
                <div class="detail-label">
                  <v-icon small class="mr-2">mdi-water</v-icon>
                  Tipo Sanguíneo
                </div>
                <!--
                  salvia-773: a edição veio para cá. Ela morava no drawer do
                  dashboard, no item que deu lugar ao próximo agendamento —
                  e era o único caminho da interface para corrigir o dado.
                -->
                <div class="detail-value d-flex align-center">
                  <span>{{ person.bloodType || "Não informado" }}</span>
                  <v-tooltip v-if="podeEditarSangue" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        x-small
                        color="primary"
                        class="ml-2"
                        v-bind="attrs"
                        v-on="on"
                        @click="abrirEdicaoSangue"
                      >
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>Editar tipo sanguíneo</span>
                  </v-tooltip>
                </div>
              </div>
            </v-col>

            <v-col cols="12" sm="6" v-if="person.cpf">
              <div class="detail-item">
                <div class="detail-label">
                  <v-icon small class="mr-2"
                    >mdi-card-account-details</v-icon
                  >
                  CPF
                </div>
                <div class="detail-value">{{ formatCpf(person.cpf) }}</div>
              </div>
            </v-col>

            <v-col cols="12" sm="6" v-if="person.status !== null">
              <div class="detail-item">
                <div class="detail-label">
                  <v-icon small class="mr-2">mdi-account-check</v-icon>
                  Status
                </div>
                <div class="detail-value">
                  <v-chip
                    :color="person.status === 1 ? 'success' : 'error'"
                    small
                    text-color="white"
                  >
                    {{ person.status === 1 ? "Ativo" : "Inativo" }}
                  </v-chip>
                </div>
              </div>
            </v-col>
          </v-row>
        </section>

        <!-- Informações de Contato -->
        <section
          class="ds-form-section"
          v-if="person.email || person.phones"
          data-tour="det-contato"
        >
          <div class="ds-form-section__head">
            <div class="ds-form-section__title">
              <v-icon small color="primary" class="mr-2">mdi-phone-outline</v-icon>
              <span>Contato</span>
            </div>
          </div>
          <v-row dense>
            <v-col cols="12" v-if="person.email">
              <div class="detail-item">
                <div class="detail-label">
                  <v-icon small class="mr-2">mdi-email</v-icon>
                  E-mail
                </div>
                <div class="detail-value">{{ person.email }}</div>
              </div>
            </v-col>

            <v-col cols="12" v-if="hasPhones">
              <div class="detail-item">
                <div class="detail-label mb-2">
                  <v-icon small class="mr-2">mdi-phone</v-icon>
                  Telefones
                </div>
                <div>
                  <v-chip
                    v-for="phone in normalizedPhones"
                    :key="phone.id"
                    :color="phone.is_primary ? 'primary' : 'default'"
                    :outlined="!phone.is_primary"
                    class="mr-2 mb-2"
                  >
                    <v-icon small left>
                      {{
                        phone.type === "celular"
                          ? "mdi-cellphone"
                          : "mdi-phone"
                      }}
                    </v-icon>
                    {{ formatPhone(phone.number) }}
                    <v-icon v-if="phone.is_primary" small right
                      >mdi-star</v-icon
                    >
                  </v-chip>
                </div>
              </div>
            </v-col>
          </v-row>
        </section>

        <!-- Endereço -->
        <section class="ds-form-section" v-if="person.address">
          <div class="ds-form-section__head">
            <div class="ds-form-section__title">
              <v-icon small color="primary" class="mr-2">mdi-map-marker</v-icon>
              <span>Endereço</span>
            </div>
          </div>
          <v-row dense>
            <v-col cols="12" v-if="person.address.street">
              <div class="detail-item">
                <div class="detail-label">
                  <v-icon small class="mr-2">mdi-road-variant</v-icon>
                  Logradouro
                </div>
                <div class="detail-value">
                  {{ person.address.street }},
                  {{ person.address.number || "S/N" }}
                </div>
              </div>
            </v-col>

            <v-col cols="12" sm="6" v-if="person.address.neighborhood">
              <div class="detail-item">
                <div class="detail-label">
                  <v-icon small class="mr-2">mdi-home-group</v-icon>
                  Bairro
                </div>
                <div class="detail-value">
                  {{ person.address.neighborhood }}
                </div>
              </div>
            </v-col>

            <v-col cols="12" sm="6" v-if="person.address.cep">
              <div class="detail-item">
                <div class="detail-label">
                  <v-icon small class="mr-2">mdi-mailbox</v-icon>
                  CEP
                </div>
                <div class="detail-value">
                  {{ formatCep(person.address.cep) }}
                </div>
              </div>
            </v-col>

            <v-col cols="12" sm="6" v-if="person.address.city">
              <div class="detail-item">
                <div class="detail-label">
                  <v-icon small class="mr-2">mdi-city</v-icon>
                  Cidade
                </div>
                <div class="detail-value">{{ person.address.city }}</div>
              </div>
            </v-col>

            <v-col cols="12" sm="6" v-if="person.address.state">
              <div class="detail-item">
                <div class="detail-label">
                  <v-icon small class="mr-2">mdi-map</v-icon>
                  Estado
                </div>
                <div class="detail-value">{{ person.address.state }}</div>
              </div>
            </v-col>
          </v-row>
        </section>
      </v-card-text>

      <!-- Actions -->
      <v-card-actions class="ds-dialog-actions">
        <v-btn outlined color="primary" @click="closeDialog">
          <v-icon left>mdi-close</v-icon>
          Cancelar
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          depressed
          :loading="loading"
          dark
          data-tour="det-editar"
          @click="editPerson()"
        >
          <v-icon left>mdi-content-save</v-icon>
          Editar
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Escolha do tipo sanguíneo (grava em patient_profiles) -->
    <v-dialog v-model="edicaoSangueAberta" max-width="320" persistent scrollable>
      <v-card class="ds-dialog">
        <v-card-title class="text-subtitle-1">Tipo Sanguíneo</v-card-title>
        <v-card-text class="ds-dialog-body">
          <section class="ds-form-section">
            <v-select
              v-model="sangueEscolhido"
              :items="opcoesSangue"
              label="Tipo sanguíneo"
              outlined
              dense
              clearable
              hide-details="auto"
            ></v-select>
          </section>
        </v-card-text>
        <v-card-actions class="ds-dialog-actions">
          <v-btn outlined color="primary" @click="edicaoSangueAberta = false">
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            depressed
            :loading="salvandoSangue"
            @click="salvarSangue"
          >
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import tourDialogMixin from "@/features/tour/tourDialogMixin";
import { formatDate } from "@/utils/datetime";
import { calculateAge } from "@/utils/calculateAge.js";
import { saveProfile } from "@/services/patientProfileService";

export default {
  name: "PersonDetailsDialog",

  mixins: [tourDialogMixin],

  props: {
    value: { type: Boolean, default: false },
    personData: { type: Object, default: () => ({}) },
    cardType: { type: String, default: "patient" },
  },

  data() {
    return {
      photoUrl: require("@/assets/images/profile.jpeg"),
      loading: false,

      // salvia-773: edição do tipo sanguíneo, que veio do drawer do dashboard.
      edicaoSangueAberta: false,
      sangueEscolhido: null,
      salvandoSangue: false,
      opcoesSangue: ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"],
    };
  },

  computed: {
    /**
     * Só paciente tem perfil clínico — o mesmo diálogo serve para médico, e ali
     * o campo é apenas informativo.
     */
    podeEditarSangue() {
      return this.cardType === "patient" && !!this.person?.id;
    },
    personAge() {
      return calculateAge(this.person.birth_date, { fallback: 0 });
    },
    tourId() {
      return "dialog-detalhes-pessoa";
    },
    internalValue: {
      get() {
        return this.value;
      },
      set(value) {
        if (!value) this.$emit("close");
      },
    },
    person() {
      return this.personData || {};
    },
    loggedUserId() {
      return this.$store.getters["auth/userId"];
    },
    loggedProfileImageUrl() {
      return this.$store.getters["auth/profileImageUrl"];
    },
    loggedProfileImageName() {
      return this.$store.getters["auth/profileImageName"];
    },
    isLoggedUser() {
      return (
        !!this.person?.id &&
        !!this.loggedUserId &&
        this.person.id === this.loggedUserId
      );
    },
    normalizedPhones() {
      if (
        this.person.phones &&
        Array.isArray(this.person.phones) &&
        this.person.phones.length > 0
      ) {
        return this.person.phones;
      }
      if (this.person.primaryPhone) {
        return [
          {
            id: "primary",
            number: this.person.primaryPhone,
            type: "celular",
            is_primary: 1,
          },
        ];
      }
      return [];
    },
    hasPhones() {
      return this.normalizedPhones.length > 0;
    },
  },

  watch: {
    async value(newVal) {
      if (!newVal) return;
      this.tourOnOpen(); // tour guiado na 1ª vez que abre
      if (this.isLoggedUser) {
        this.loading = true;
        try {
          // se ainda não tem url mas tem name, busca blob
          if (!this.loggedProfileImageUrl && this.loggedProfileImageName) {
            await this.$store.dispatch("auth/fetchProfileImage");
          }
          this.photoUrl =
            this.$store.getters["auth/profileImageUrl"] ||
            require("@/assets/images/profile.jpeg");
        } finally {
          this.loading = false;
        }
        return;
      }
      this.photoUrl =
        this.person.profile_image_url ||
        require("@/assets/images/profile.jpeg");
    },
  },

  methods: {
    formatDateBr: (v) => formatDate(v, { fallback: "Não informado" }),

    abrirEdicaoSangue() {
      // A listagem monta o paciente com `bloodType: "N/A"` quando nao ha valor,
      // e o dashboard com null. "N/A" nao esta entre as opcoes -- preselecionar
      // deixaria o seletor com um valor que ele nao reconhece.
      const atual = this.person.bloodType;
      this.sangueEscolhido = this.opcoesSangue.includes(atual) ? atual : null;

      this.edicaoSangueAberta = true;
    },

    async salvarSangue() {
      if (!this.person?.id) return;

      this.salvandoSangue = true;

      try {
        const valor = this.sangueEscolhido || null;
        await saveProfile(this.person.id, { blood_type: valor });

        // Atualiza o que está na tela sem esperar o pai recarregar; quem
        // renderiza o diálogo decide se quer recarregar, ouvindo o evento.
        this.$set(this.person, "bloodType", valor);
        this.edicaoSangueAberta = false;
        this.$emit("blood-type-updated", valor);
      } catch (error) {
        console.error("Erro ao salvar tipo sanguíneo:", error);
      } finally {
        this.salvandoSangue = false;
      }
    },
    tourSteps() {
      return [
        {
          anchor: "det-header",
          title: "Ficha da pessoa",
          description:
            "Esta janela mostra os dados cadastrais da pessoa (só leitura). No topo ficam a foto, o tipo (paciente ou médico) e a idade.",
        },
        {
          anchor: "det-info",
          title: "Informações pessoais",
          description:
            "Nome completo, data de nascimento, gênero e demais dados básicos do cadastro.",
        },
        {
          anchor: "det-contato",
          title: "Contato",
          description:
            "Telefones (com indicação do principal) e e-mail — usados nos convites de atendimento e na comunicação com a pessoa.",
        },
        {
          anchor: "det-editar",
          title: "Editar os dados",
          description:
            "Precisa corrigir algo? “Editar” abre o formulário de cadastro já preenchido, para você ajustar e salvar.",
        },
      ];
    },
    closeDialog() {
      this.$emit("input", false);
    },
    editPerson() {
      this.$emit("edit-person", this.personData.id);
      this.closeDialog();
    },
    formatPhone(phoneNumber) {
      if (!phoneNumber) return "";
      const cleaned = phoneNumber.replace(/\D/g, "");
      if (cleaned.length === 11) {
        return `(${cleaned.substr(0, 2)}) ${cleaned.substr(
          2,
          5,
        )}-${cleaned.substr(7)}`;
      }
      return phoneNumber;
    },
    formatCpf(cpf) {
      if (!cpf) return "";
      const cleaned = cpf.replace(/\D/g, "");
      return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    },
    formatCep(cep) {
      if (!cep) return "";
      const cleaned = cep.replace(/\D/g, "");
      return cleaned.replace(/(\d{5})(\d{3})/, "$1-$2");
    },
  },
};
</script>

<style scoped>
/* ═══ Design system dialog (compartilhado entre dialogs) ═══ */
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}

.ds-dialog-header {
  background: linear-gradient(135deg, var(--ds-primary) 0%, var(--ds-primary-deep) 100%) !important;
  color: var(--ds-white) !important;
  padding: 0 !important;
  position: relative;
}

.header-content {
  position: relative;
  z-index: 1;
}

.close-btn {
  position: relative;
  z-index: 1;
  margin-right: 12px;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

.detail-item {
  padding: 8px 0;
}

.detail-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--ds-text-muted);
  letter-spacing: 0.6px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
}

.detail-label :deep(.v-icon) {
  color: var(--ds-primary) !important;
}

.detail-value {
  font-size: 13px;
  color: var(--ds-text-strong);
  font-weight: 600;
  line-height: 1.5;
}
</style>
